import { CheckIcon } from '@heroicons/react/16/solid';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { KeyboardEvent, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Badge } from '@/components/ui/daisy/badge';
import { Button } from '@/components/ui/daisy/button';
import { Dropdown } from '@/components/ui/daisy/dropdown';
import { Menu } from '@/components/ui/daisy/menu';
import { KeyboardKeys } from '@/utils/Keyboard';
import { preventDefault } from '@/utils/mouseEvent';

import {
  chipSizeClasses,
  colorClasses,
  sizeClasses,
  variantClasses,
} from './classes';
import { MultiSelectContext } from './context';
import {
  MultiSelectContextValue,
  SelectMultipleProps,
  SelectOptionProps,
} from './types';
import { useMultiSelect } from './useMultiSelect';

const DEFAULT_MAX_VISIBLE_CHIPS = 6;

const closeOnEscape = (event: KeyboardEvent<HTMLDivElement>) => {
  if (event.key !== KeyboardKeys.Escape) return;
  if (event.target instanceof HTMLElement) event.target.blur();
};

export const MultiSelect = ({
  value,
  onChange,
  onClear,
  placeholder,
  maxVisibleChips = DEFAULT_MAX_VISIBLE_CHIPS,
  disabled = false,
  size = 'md',
  color = 'default',
  variant = 'default',
  error = false,
  className,
  children,
}: SelectMultipleProps) => {
  const { t } = useTranslation('translation', { keyPrefix: 'select' });
  const { t: tCommon } = useTranslation('translation', {
    keyPrefix: 'common',
  });

  const {
    selectedValues,
    selectedOptions,
    handleToggle,
    registerOption,
    unregisterOption,
  } = useMultiSelect({ value, onChange, disabled });

  const visibleOptions = selectedOptions.slice(0, maxVisibleChips);
  const showClear = !!onClear && selectedValues.length > 0 && !disabled;
  const hiddenCount = selectedOptions.length - visibleOptions.length;
  const chipSize = chipSizeClasses[size];

  const contextValue = useMemo<MultiSelectContextValue>(
    () => ({
      selectedValues,
      onToggle: handleToggle,
      registerOption,
      unregisterOption,
    }),
    [selectedValues, handleToggle, registerOption, unregisterOption],
  );

  return (
    <MultiSelectContext.Provider value={contextValue}>
      <Dropdown
        onKeyDown={closeOnEscape}
        className={twMerge('w-full min-w-0', className)}
      >
        <Dropdown.Trigger disabled={disabled} className="relative">
          <div
            className={twMerge(
              'select w-full h-auto min-h-(--size) py-1 cursor-pointer flex items-center',
              sizeClasses[size],
              colorClasses[color],
              variantClasses[variant],
              error && 'select-error',
              disabled && 'select-disabled',
              showClear && 'pr-16',
            )}
          >
            <ConditionalRenderer
              condition={selectedOptions.length > 0}
              fallback={
                <span className="text-base-content/50 truncate">
                  {placeholder ?? tCommon('select')}
                </span>
              }
            >
              <div className="flex flex-wrap gap-1 items-center min-w-0">
                {visibleOptions.map(option => (
                  <Badge
                    key={option.value}
                    variant="soft"
                    size={chipSize}
                    className="shrink-0 gap-1 pr-1"
                  >
                    {option.label}
                    <button
                      type="button"
                      disabled={disabled}
                      aria-label={t('removeOption', { label: option.label })}
                      className="pointer-events-auto rounded-full hover:bg-base-content/10"
                      onMouseDown={preventDefault}
                      onClick={() => handleToggle(option.value)}
                    >
                      <XMarkIcon className="size-3" />
                    </button>
                  </Badge>
                ))}
                <ConditionalRenderer condition={hiddenCount > 0}>
                  <Badge variant="ghost" size={chipSize} className="shrink-0">
                    +{hiddenCount}
                  </Badge>
                </ConditionalRenderer>
              </div>
            </ConditionalRenderer>
          </div>

          <ConditionalRenderer condition={showClear}>
            <Button
              aria-label={tCommon('clear')}
              className="pointer-events-auto absolute right-10 top-1/2 -translate-y-1/2"
              variant="ghost"
              size="xs"
              shape="circle"
              type="button"
              tabIndex={-1}
              onMouseDown={preventDefault}
              onClick={onClear}
            >
              <XMarkIcon className="size-4 text-error" />
            </Button>
          </ConditionalRenderer>
        </Dropdown.Trigger>

        <Dropdown.Content className="z-10 w-full!">
          <Menu
            role="listbox"
            aria-multiselectable
            className="w-full bg-base-100 rounded-box shadow-sm p-2 max-h-60 flex-nowrap overflow-y-auto"
            activeColor="neutral"
          >
            {children}
          </Menu>
        </Dropdown.Content>
      </Dropdown>
    </MultiSelectContext.Provider>
  );
};

type MultiSelectOptionProps = SelectOptionProps & {
  multiSelect: MultiSelectContextValue;
};

export const MultiSelectOption = ({
  value,
  children,
  disabled = false,
  className,
  multiSelect,
}: MultiSelectOptionProps) => {
  const { selectedValues, onToggle, registerOption, unregisterOption } =
    multiSelect;

  const stringValue = String(value);
  const isSelected = selectedValues.includes(stringValue);

  const childrenRef = useRef(children);
  childrenRef.current = children;

  useEffect(() => {
    registerOption(stringValue, childrenRef.current);
    return () => unregisterOption(stringValue);
  }, [stringValue, registerOption, unregisterOption]);

  return (
    <Menu.Item
      role="option"
      aria-selected={isSelected}
      disabled={disabled}
      className={className}
    >
      <button
        type="button"
        disabled={disabled}
        onMouseDown={preventDefault}
        onClick={() => onToggle(stringValue)}
        className="flex items-center"
      >
        <ConditionalRenderer
          condition={isSelected}
          fallback={<div className="size-4" />}
        >
          <CheckIcon className="size-4 text-base-content/50" />
        </ConditionalRenderer>

        {children}
      </button>
    </Menu.Item>
  );
};
